import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Barcode, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/publish");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8F9FA]">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#09090B] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1533135091724-62cc5402aa20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBsaW5lcyUyMHBhdHRlcm58ZW58MHx8fHwxNzgzMTM1MjI2fDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(100%) contrast(1.1)",
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-2 font-display font-bold text-xl">
            <Barcode className="w-6 h-6" />
            <span>MAGIC SIP</span>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="eyebrow text-white/70">Utilitário Retail / E-commerce</div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold leading-tight">
            Encontre imagens de<br />produto em lote,<br />
            <span className="brand-text" style={{ color: "#7AA2FF" }}>
              por Nome e Categoria.
            </span>
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed text-sm">
            Envie uma planilha com até <strong className="text-white">10.000 produtos</strong> apenas
            com <strong className="text-white">Nome, Categoria e Preço</strong>. A IA faz todo o resto —
            busca fotos, descrição, indicação de uso e SEO automaticamente.
          </p>
          <p className="text-white/60 max-w-md leading-relaxed text-xs">
            Tenha seu cadastro completo em <strong className="text-white">poucas horas</strong> ao
            invés de perder dias — ou meses — preenchendo manualmente.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/50 font-mono">
          v1.0 · UTILITÁRIO PARA E-COMMERCE
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-3">Acesso</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-2">
            Entrar na plataforma
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            Não tem uma conta?{" "}
            <Link to="/register" className="brand-text font-medium hover:underline" data-testid="link-to-register">
              Criar conta gratuita
            </Link>
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                className="mt-2 h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Senha</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-11 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="text-sm rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md brand-primary hover:opacity-95 text-white font-medium transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? "Entrando..." : "Entrar"}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            <p className="text-xs text-zinc-500 font-mono">
              Conta demo: admin@example.com / admin123
            </p>
            <div className="mt-3 flex gap-4 text-xs text-zinc-400 font-mono">
              <Link to="/privacy" className="hover:text-[#002FA7]" data-testid="login-privacy-link">
                Política de Privacidade
              </Link>
              <Link to="/support" className="hover:text-[#002FA7]" data-testid="login-support-link">
                Suporte
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
